import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Mail, Phone, Linkedin, Loader2 } from 'lucide-react';
import { getTeamMembers } from '../services/api';
import robWisemanPhoto from '../assets/rob-wiseman-headshot.jpg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Map of team member names to their bundled photos
const bundledPhotos = {
  'Rob Wiseman': robWisemanPhoto
};

const TeamPage = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        setLoading(true);
        const data = await getTeamMembers();
        setTeamMembers(data);
        setError(null);
      } catch (err) {
        setError('Failed to load team members');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();
  }, []);

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Our Team | Close Now Lending - Meet Our Lending Experts"
        description="Meet the experienced team at Close Now Lending. Our real estate lending professionals bring decades of combined expertise in hard money, fix and flip, DSCR, and commercial financing."
        keywords="Close Now Lending team, real estate lending experts, hard money loan officers, investment loan specialists, Houston lending professionals"
        url="/team"
      />
      <Header />
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Meet Our <span className="text-blue-600">Team</span>
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              Dedicated professionals committed to your success
            </p>
          </div>
        </div>
      </section>

      {/* Team Members */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <Loader2 className="h-12 w-12 text-blue-600 animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center text-red-600 py-12">
              <p>{error}</p>
            </div>
          ) : teamMembers.length === 0 ? (
            <div className="text-center text-gray-600 py-12">
              <p>No team members found.</p>
            </div>
          ) : (
            <div className="max-w-7xl mx-auto">
              <div className={`grid gap-8 ${
                teamMembers.length === 1 
                  ? 'grid-cols-1 max-w-md mx-auto' 
                  : teamMembers.length === 2 
                  ? 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto' 
                  : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
              }`}>
                {teamMembers.map((member) => (
                  <Card 
                    key={member.id} 
                    className="overflow-hidden group hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-blue-200"
                  >
                    {/* Image */}
                    <div className="relative overflow-hidden bg-gray-100">
                      <img 
                        src={
                          bundledPhotos[member.name]
                            ? bundledPhotos[member.name]
                            : member.image.startsWith('data:')
                            ? member.image
                            : member.image.startsWith('/api/uploads') || member.image.startsWith('/uploads')
                            ? `${BACKEND_URL}${member.image}`
                            : member.image
                        }
                        alt={`${member.name} - ${member.title} at Close Now Lending`}
                        className="w-full object-cover object-top aspect-[3/4]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-6 right-6">
                        <h3 className="text-2xl font-bold text-white mb-1">
                          {member.name}
                        </h3>
                        <p className="text-blue-200 font-semibold">
                          {member.title}
                        </p>
                      </div>
                    </div>

                    <CardContent className="p-6">
                      <p className="text-gray-700 mb-6 leading-relaxed">
                        {member.bio}
                      </p>

                      {/* Contact Info */}
                      <div className="space-y-2 pt-4 border-t border-gray-200">
                        <a 
                          href={`mailto:${member.email}`} 
                          className="flex items-center text-gray-600 hover:text-blue-600 transition-colors text-sm"
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          {member.email}
                        </a>
                        <a 
                          href={`tel:+1${member.phone.replace(/\D/g, '')}`} 
                          className="flex items-center text-gray-600 hover:text-blue-600 transition-colors text-sm"
                        >
                          <Phone className="h-4 w-4 mr-2" />
                          {member.phone}
                        </a>
                        <a 
                          href={member.linkedin} 
                          className="flex items-center text-gray-600 hover:text-blue-600 transition-colors text-sm"
                        >
                          <Linkedin className="h-4 w-4 mr-2" />
                          LinkedIn Profile
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to Work With Us?
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Our team is here to help you achieve your real estate investment goals.
            </p>
            <a 
              href="tel:+19034762937"
              className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-lg"
            >
              Contact Us Today
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TeamPage;